import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/Button";
import { CreditCard, Info } from "lucide-react";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";
import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { updateQuickPayment } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";
import { useAuth } from "../../auth/AuthContext";

const QuickPaymentsSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();
  const { syncSettingsToLocalStorage } = useAuth();
  const [quickPaymentType, setQuickPaymentType] = useState<"stylist" | "direct">("direct");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settingsData && settingsData.settings) {
      console.log("quick -", settingsData.settings.quick_payment_type);
      setQuickPaymentType(settingsData.settings.quick_payment_type as "stylist" | "direct");
    }
  }, [settingsData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateQuickPayment(quickPaymentType);
      toast.success("Quick payment settings updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
      console.log("Quick payment settings updated");
    } catch (error: any) {
      console.error("Failed to update quick payment settings", error);
      toast.error("Failed to update payment settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading payment settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Quick Payments"
        description="Configure quick payment options for stylists"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <CreditCard size={18} className="text-white" />
            Payment Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            {/* Payment Settings Section */}
            <div>
              <SectionHeader title="Payment Settings" />

              {/* Info Box */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Enable quick payment mode to allow stylists to process
                  payments faster
                </p>
              </div>

              {/* Quick Payment Toggle */}
              <ToggleSetting
                label="Quick Payment Mode"
                description="Enable quick payment functionality for stylists"
                checked={quickPaymentType === "stylist"}
                onChange={(enabled) => setQuickPaymentType(enabled ? "stylist" : "direct")}
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickPaymentsSettings;
